import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Layouts, MemberSearchField } from 'components'
import { head } from 'helpers'
import * as api from 'api'
import s from './adminForm.module.scss'

const EXPERIENCE_OPTIONS = ['Fresher', '0-1 years', '1-2 years', '2-3 years', '3-6 years', '6-10 years', '10+ years']
const JOB_TYPES = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'remote', label: 'Remote' },
  { value: 'freelance', label: 'Freelance' },
  { value: 'contract', label: 'Contract' },
]
const MAX_SKILLS = 15

const initialForm = {
  company_name: '',
  job_title: '',
  job_description: '',
  salary: '',
  job_type: '',
  state: '',
  city: '',
  experience: '',
  application_deadline: '',
  contact_number: '',
  fkUserId: '',
  declaration: false,
}

export default function AdminJobCreate() {
  const [form, setForm] = useState(initialForm)
  const [skills, setSkills] = useState([])
  const [skillInput, setSkillInput] = useState('')
  const [states, setStates] = useState([])
  const [cities, setCities] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    head({ title: 'Create Job Opening | BCN Admin' })
  }, [])

  useEffect(() => {
    api.publicApi.locations.fetchAllStates({ limit: 100, state: '' }).then(res => {
      if (res.code === 200) setStates(res.payload?.states || [])
    }).catch(() => setStates([]))
  }, [])

  const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }))

  const onStateChange = useCallback(async (stateName) => {
    setField('state', stateName)
    setField('city', '')
    if (!stateName) {
      setCities([])
      return
    }
    try {
      const stateRes = await api.publicApi.locations.fetchAllStates({ limit: 200, state: stateName })
      if (stateRes.code === 200 && stateRes.payload?.states?.length) {
        const fkStateId = stateRes.payload.states[0]._id
        const cityRes = await api.publicApi.locations.fetchAllCitiesViaState({ limit: 500, city: '', fkStateId })
        if (cityRes.code === 200) setCities(cityRes.payload?.cities || [])
        else setCities([])
      }
    } catch {
      setCities([])
    }
  }, [])

  const addSkill = () => {
    if (skills.length >= MAX_SKILLS) return
    const val = skillInput.trim()
    if (val && !skills.includes(val)) {
      setSkills(prev => [...prev, val])
      setSkillInput('')
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!form.declaration) {
      Alert.warn('Please accept the declaration')
      return
    }
    if (skills.length === 0) {
      Alert.warn('Please add at least one required skill before submitting.')
      return
    }
    if (!form.job_type) {
      Alert.warn('Please select a job type')
      return
    }
    if (!form.experience) {
      Alert.warn('Please select experience')
      return
    }
    const contact = form.contact_number.replace(/\D/g, '').slice(0, 10)
    if (!/^[0-9]{10}$/.test(contact)) {
      Alert.warn('Contact number must be exactly 10 digits')
      return
    }
    setLoading(true)
    try {
      const payload = {
        ...form,
        required_skills: skills,
        contact_number: contact,
        fkUserId: form.fkUserId?.trim() || undefined,
      }
      const res = await api.admin.jobs.create(payload)
      if (res.code === 201) {
        Alert.success(res.message || 'Job created successfully')
        setForm(initialForm)
        setSkills([])
        setSkillInput('')
      } else {
        Alert.error(res.message || 'Failed to create job')
      }
    } catch {
      Alert.error('Failed to create job opening')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={s.page}>
      <Layouts.Classic title="Create Job Opening">
        <Link className={s.secondaryBtn} to="/admin/approvalRequests">
          <span className="material-icons-outlined">pending_actions</span>
          Pending Jobs
        </Link>
      </Layouts.Classic>

      <div className={s.hero}>
        <p className={s.eyebrow}>BCN Careers Admin</p>
        <p className={s.heroText}>
          Post a job on behalf of an employer. Admin-created jobs are published immediately without approval.
        </p>
      </div>

      <form className={s.formCard} onSubmit={handleSubmit}>
        <h2 className={s.sectionTitle}>Company & Role</h2>
        <div className={s.grid}>
          <div className={s.field}>
            <label>Company Name *</label>
            <input value={form.company_name} onChange={e => setField('company_name', e.target.value)} required />
          </div>
          <div className={s.field}>
            <label>Job Title *</label>
            <input value={form.job_title} onChange={e => setField('job_title', e.target.value)} required />
          </div>
          <div className={`${s.field} ${s.fullWidth}`}>
            <label>Job Description *</label>
            <textarea value={form.job_description} onChange={e => setField('job_description', e.target.value)} required />
          </div>
          <div className={s.field}>
            <label>Salary (per annum) *</label>
            <input
              value={form.salary}
              onChange={e => {
                const value = e.target.value
                if (/^\d{0,10}$/.test(value)) setField('salary', value)
              }}
              placeholder="Salary per annum"
              inputMode="numeric"
              required
            />
          </div>
          <div className={s.field}>
            <label>Job Type *</label>
            <select value={form.job_type} onChange={e => setField('job_type', e.target.value)} required>
              <option value="">Select Job Type</option>
              {JOB_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className={s.field}>
            <label>Experience *</label>
            <select value={form.experience} onChange={e => setField('experience', e.target.value)} required>
              <option value="">Select Experience</option>
              {EXPERIENCE_OPTIONS.map(exp => <option key={exp} value={exp}>{exp}</option>)}
            </select>
          </div>
          <div className={`${s.field} ${s.fullWidth}`}>
            <label>Required Skills * (max {MAX_SKILLS})</label>
            <div className={s.skillInputWrap}>
              <input
                value={skillInput}
                onChange={e => setSkillInput(e.target.value)}
                placeholder="Add a skill and press Add"
                disabled={skills.length >= MAX_SKILLS}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addSkill()
                  }
                }}
              />
              <button type="button" className={s.addBtn} onClick={addSkill} disabled={skills.length >= MAX_SKILLS}>Add</button>
            </div>
            {skills.length > 0 && (
              <div className={s.chips}>
                {skills.map(skill => (
                  <span key={skill} className={s.chip}>
                    {skill}
                    <button type="button" onClick={() => setSkills(prev => prev.filter(x => x !== skill))} aria-label={`Remove ${skill}`}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <h2 className={s.sectionTitle}>Location & Contact</h2>
        <div className={s.grid}>
          <div className={s.field}>
            <label>State *</label>
            <select value={form.state} onChange={e => onStateChange(e.target.value)} required>
              <option value="">Select state</option>
              {states.slice().sort((a, b) => a.state.localeCompare(b.state)).map(st => (
                <option key={st._id} value={st.state}>{st.state}</option>
              ))}
            </select>
          </div>
          <div className={s.field}>
            <label>City *</label>
            <select value={form.city} onChange={e => setField('city', e.target.value)} disabled={!form.state} required>
              <option value="">{form.state ? 'Select city' : 'Select state first'}</option>
              {cities.slice().sort((a, b) => a.city.localeCompare(b.city)).map(ct => (
                <option key={ct._id} value={ct.city}>{ct.city}</option>
              ))}
            </select>
          </div>
          <div className={s.field}>
            <label>Application Deadline *</label>
            <input type="date" value={form.application_deadline} onChange={e => setField('application_deadline', e.target.value)} required />
          </div>
          <div className={s.field}>
            <label>Contact Number *</label>
            <input
              value={form.contact_number}
              onChange={e => setField('contact_number', e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="10-digit mobile"
              inputMode="numeric"
              required
            />
          </div>
          <div className={`${s.field} ${s.fullWidth}`}>
            <MemberSearchField
              value={form.fkUserId}
              onChange={id => setField('fkUserId', id)}
            />
          </div>
        </div>

        <label className={s.checkboxRow}>
          <input type="checkbox" checked={form.declaration} onChange={e => setField('declaration', e.target.checked)} />
          <span>I confirm this job posting information is accurate and authorized for publication on BCN.</span>
        </label>

        <p className={s.note}>This job will be published immediately as Open and Approved.</p>

        <div className={s.actions}>
          <button type="submit" className={s.submitBtn} disabled={loading}>
            {loading ? 'Creating…' : 'Create & Publish Job'}
          </button>
        </div>
      </form>
    </div>
  )
}